import { COMPANY_ROLE, TIME_SHEET_SIGNATURE_TYPE, TIME_SHEET_STATUS } from '@/models/references/references';
import type { CurrentCompany } from '@/stores/useCompanyStore';

/** Qui valide, dans la société sélectionnée : Dirigeant, ou manager de certaines équipes */
export interface Reviewer {
  isDirector: boolean;
  /** Équipes dont il est le manager actif */
  managedTeamIds: number[];
  /** Son rattachement à la société : ni sa propre feuille, ni sa propre absence */
  memberId: number | null;
}

export function reviewerOf(company: CurrentCompany | null): Reviewer {
  return {
    isDirector: company?.companyRole === COMPANY_ROLE.DIRECTOR,
    managedTeamIds: company?.managedTeamIds ?? [],
    memberId: company?.memberId ?? null,
  };
}

/** Ce qu'il faut d'une feuille pour décider (TimeSheetModel en a tout) */
export interface ReviewedSheet {
  memberId: number;
  teamId: number;
  timeSheetStatusReference: string;
  isAbsence: boolean;
}

export interface ReviewActions {
  /** Valider = signer : en Dirigeant (validation définitive) ou en Manager (passage au Dirigeant) ; null : pas de validation */
  validateAs: typeof TIME_SHEET_SIGNATURE_TYPE.DIRECTOR | typeof TIME_SHEET_SIGNATURE_TYPE.MANAGER | null;
  canReject: boolean;
}

const NONE: ReviewActions = { validateAs: null, canReject: false };

/** Le manager de cette équipe, pour la feuille d'un autre que lui */
function managesFor(reviewer: Reviewer, memberId: number, teamId: number): boolean {
  return reviewer.managedTeamIds.includes(teamId) && memberId !== reviewer.memberId;
}

/**
 * Actions sur une feuille, comme les règles de l'API (workflow des feuilles) : le Dirigeant valide ou refuse une feuille
 * en attente (du manager ou de lui) ; le manager, celle de son équipe qui l'attend, jamais la sienne. Refuser une absence
 * l'annule.
 */
export function reviewActions(reviewer: Reviewer, sheet: ReviewedSheet): ReviewActions {
  const status = sheet.timeSheetStatusReference;
  if (reviewer.isDirector) {
    if (status !== TIME_SHEET_STATUS.PENDING_MANAGER && status !== TIME_SHEET_STATUS.PENDING_DIRECTOR) return NONE;
    return { validateAs: TIME_SHEET_SIGNATURE_TYPE.DIRECTOR, canReject: true };
  }
  if (status !== TIME_SHEET_STATUS.PENDING_MANAGER || !managesFor(reviewer, sheet.memberId, sheet.teamId)) return NONE;
  return { validateAs: TIME_SHEET_SIGNATURE_TYPE.MANAGER, canReject: true };
}

/** Déclarer l'absence d'un employé qui n'a pas soumis : le Dirigeant, ou le manager de son équipe ; jamais la sienne */
export function canDeclareAbsence(reviewer: Reviewer, memberId: number, teamId: number): boolean {
  if (memberId === reviewer.memberId) return false;
  return reviewer.isDirector || managesFor(reviewer, memberId, teamId);
}
